/**
 * RBAC-3.6 · The unsaved-changes banner's two lines (UX-DR10, AC #1).
 *
 * Two subjects, deliberately: "3 unsaved changes" is about the admin, "Affects 12 people, including
 * you." is about the consequence. Naming only the first is the common failure UX-DR10 names.
 *
 * ⚠️ "including you" is a claim (NFR-13). It comes from `includesCaller` on `role_holder_count()`,
 * which reads both role systems, never from comparing the caller's own `users.role_id`, which misses a
 * caller who holds the role only through `user_roles`.
 *
 * Pure, no DOM, no Supabase.
 */

import type { RoleHolders } from './rolePermissionDiff';

export interface BannerCopy {
  title: string;
  /** Says so while the count is loading or could not be read, rather than showing no number. */
  affects: string;
}

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);

export function bannerTitle(changes: number): string {
  return changes === 1 ? '1 unsaved change' : `${changes} unsaved changes`;
}

export function bannerAffects(holders: RoleHolders | null, state: 'loading' | 'error' | 'ready' = holders ? 'ready' : 'loading'): string {
  if (state === 'error') return 'Who this affects could not be counted. Review the changes to see it before saving.';
  if (!holders || state === 'loading') return 'Counting who this affects…';
  const { count, includesCaller, outsideAccount } = holders;
  if (count === 0) return 'Affects nobody yet: no one holds this role.';
  if (count === 1 && includesCaller) return 'Affects 1 person: you.';
  const elsewhere =
    outsideAccount === 0 ? ''
      : outsideAccount === count ? (count === 1 ? ', who holds it through another account' : ', all through another account')
        : `, ${outsideAccount} of them through another account`;
  return `Affects ${people(count)}${includesCaller ? ', including you' : ''}${elsewhere}.`;
}

export function bannerCopy(changes: number, holders: RoleHolders | null, state?: 'loading' | 'error' | 'ready'): BannerCopy {
  return { title: bannerTitle(changes), affects: bannerAffects(holders, state) };
}
